import { Grid } from "@material-ui/core";
import moment from "moment";
import { FunctionComponent, useMemo } from "react";
import { Option } from "space-monad";
import styled from "styled-components";
import { Checkbox } from "../../../components/forms/checkbox";
import { KycIcon } from "../../../components/icons/status";
import { WrapGridCell } from "../../../components/layout";
import { Table } from "../../../components/table";
import { Widget } from "../../../components/widget";
import { countries } from "../../../store/common/models";
import { validationFields } from "../../../store/common/models/fields";
import { VerificationDTO } from "../../../store/verification/models/dto/kyc";

const StyledWidget = styled(Widget)`
  &:not(:last-child) {
    margin-bottom: 1em;
  }
`;

const KycStatus = styled.div`
  margin-right: 1em;
`;

const Label = styled.span`
  font-weight: 500;
`;

export const SingleKyb: FunctionComponent<{
  fraudCheckId: string;
  item: VerificationDTO.Responses.GetSingleKyb.T;
}> = ({ item }) => {
  const {
    amlResponse,
    companyJurisdictionCountryCode,
    companyRegistrationNumber,
    createdDate,
    email,
    status,
    verificationData,
  } = item;

  const companyRegistrationNumberName = validationFields.companyRegistrationNumber.useName();
  const companyJurisdictionCountryCodeName = validationFields.companyJurisdictionCountryCode.useName();

  const formattedDate = useMemo(
    () => moment.utc(createdDate).local().format("yyyy-MM-DD HH:mm"),
    [createdDate]
  );
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <StyledWidget title="Overview">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Label>{companyRegistrationNumberName}:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {companyRegistrationNumber}
                  </Grid>
                  <Grid item xs={6}>
                    <Label>Name:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {verificationData?.kyb?.companyName ?? "n/a"}
                  </Grid>
                  <WrapGridCell xs={6}>
                    <Label>Address:</Label>
                  </WrapGridCell>
                  <Grid item xs={6}>
                    {verificationData?.kyb?.companyRegisteredAddressInFull ??
                      "n/a"}
                  </Grid>
                  <Grid item xs={6}>
                    <Label>{companyJurisdictionCountryCodeName}:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {Option(companyJurisdictionCountryCode).fold(
                      () => "n/a",
                      (x) =>
                        countries.enum[x.toUpperCase() as typeof countries.T] ||
                        x
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Label>Company Status:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {verificationData?.kyb?.companyCurrentStatus ?? "n/a"}
                  </Grid>
                  <Grid item xs={6}>
                    <Label>Email:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {email || "n/a"}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Label>Status:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    <KycStatus>{KycIcon(status, "kyb")}</KycStatus>
                  </Grid>
                  <Grid item xs={6}>
                    <Label>Date Submitted:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    <KycStatus>{formattedDate}</KycStatus>
                  </Grid>
                  <Grid item xs={6}>
                    <Label>Number Of Employees:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {verificationData?.kyb?.companyNumberOfEmployees ?? "n/a"}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </StyledWidget>
        </Grid>
        {Option(amlResponse).fold(
          () => null,
          ({ declineCodes, filters }) => (
            <Grid item xs={12}>
              <StyledWidget title="Response Information">
                <Grid container spacing={3}>
                  {Option(declineCodes).fold(
                    () => null,
                    (x) => (
                      <>
                        <Grid item xs={6} sm={3}>
                          <Label>Decline Codes:</Label>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          {x.map((x) => x.message)}
                        </Grid>
                      </>
                    )
                  )}
                  {Option(filters).fold(
                    () => null,
                    (x) => (
                      <>
                        <Grid item xs={6} sm={3}>
                          <Label>Filters:</Label>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          {x.join(", ")}
                        </Grid>
                      </>
                    )
                  )}
                </Grid>
              </StyledWidget>
            </Grid>
          )
        )}

        <Grid item xs={12}>
          <StyledWidget title="More Details">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Label>Company Type:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {verificationData?.kyb?.companyType ?? "n/a"}
                  </Grid>
                  <Grid item xs={6}>
                    <Label>Ultimate Beneficial Owners:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {Option(verificationData)
                      .map((x) => x.kyb)
                      .map((x) => x.companyUltimateBeneficialOwners)
                      .map((owners) =>
                        owners.map(({ name }) => name).join(", ")
                      )
                      .get() ?? "n/a"}
                  </Grid>
                  <Grid item xs={6}>
                    <Label>Registry URL:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {Option(verificationData)
                      .map((x) => x.kyb)
                      .map((x) => x.companyRegistryUrl)
                      .map((url) => (
                        <a target="_blank" rel="noreferrer" href={url}>
                          {url}
                        </a>
                      ))
                      .get() ?? "n/a"}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Label>Incorporation Date:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {Option(verificationData)
                      .map((x) => x.kyb)
                      .map((x) => x.companyIncorporationDate)
                      .fold(
                        () => "n/a",
                        (date) => moment.utc(date).local().format("DD-MM-yyyy")
                      )}
                  </Grid>
                  <Grid item xs={6}>
                    <Label>Dissolution Date:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {Option(verificationData)
                      .map((x) => x.kyb)
                      .map((x) => x.companyDissolutionDate)
                      .fold(
                        () => "n/a",
                        (date) => moment.utc(date).local().format("DD-MM-yyyy")
                      )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </StyledWidget>
        </Grid>
        {Option(verificationData?.kyb?.companySource).fold(
          () => null,
          ({ publisher, url, terms, retrievedAt }) => (
            <Grid item xs={12}>
              <StyledWidget title="Source">
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Label>Publisher:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {url ? (
                      <a target="_blank" rel="noreferrer" href={url}>
                        {publisher ?? "n/a"}
                      </a>
                    ) : (
                      publisher ?? "n/a"
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Label>Terms:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {terms ?? "n/a"}
                  </Grid>
                  <Grid item xs={6}>
                    <Label>Retreived Date:</Label>
                  </Grid>
                  <Grid item xs={6}>
                    {retrievedAt
                      ? moment
                          .utc(retrievedAt)
                          .local()
                          .format("yyyy-MM-DD HH:mm")
                      : "n/a"}
                  </Grid>
                </Grid>
              </StyledWidget>
            </Grid>
          )
        )}

        {Option(verificationData?.kyb?.companyOfficers)
          .filter((x) => x.length > 0)
          .fold(
            () => null,
            (codes) => (
              <Grid item xs={12}>
                <StyledWidget title="Company Officers">
                  <Table
                    data={codes}
                    head={[
                      { name: "name", title: "Name" },
                      { name: "position", title: "Position" },
                      {
                        name: "occupation",
                        title: "Occupation",
                        hiddenMobile: true,
                      },
                      { name: "address", title: "Address", hiddenMobile: true },
                      { name: "currentStatus", title: "Current Status" },
                      {
                        name: "inactive",
                        title: "Inactive",
                        hiddenMobile: true,
                      },
                      {
                        name: "startDate",
                        title: "Start Date",
                        hiddenMobile: true,
                      },
                      {
                        name: "endDate",
                        title: "End Date",
                        hiddenMobile: true,
                      },
                    ]}
                    renderFields={{
                      startDate: ({ startDate }) =>
                        startDate ? (
                          <>
                            {moment.utc(startDate).local().format("DD-MM-yyyy")}
                          </>
                        ) : null,
                      endDate: ({ endDate }) =>
                        endDate ? (
                          <>
                            {moment.utc(endDate).local().format("DD-MM-yyyy")}
                          </>
                        ) : null,
                      inactive: ({ inactive }) =>
                        inactive !== null ? (
                          <Checkbox checked={inactive} disabled />
                        ) : null,
                    }}
                  ></Table>
                </StyledWidget>
              </Grid>
            )
          )}
        {Option(verificationData?.kyb?.companyFilings)
          .filter((x) => x.length > 0)
          .fold(
            () => null,
            (codes) => (
              <Grid item xs={12}>
                <StyledWidget title="Company Filings">
                  <Table
                    data={codes}
                    head={[
                      { name: "title", title: "Title" },
                      { name: "date", title: "Date" },
                      { name: "referenceUrl", title: "Reference Url" },
                    ]}
                    renderFields={{
                      title: ({ title, referenceUrl }) =>
                        Option(referenceUrl).fold(
                          () => <>{title}</>,
                          (url) => (
                            <a target="_blank" rel="noreferrer" href={url}>
                              {title}
                            </a>
                          )
                        ),
                    }}
                    hide={["referenceUrl"]}
                  ></Table>
                </StyledWidget>
              </Grid>
            )
          )}
        {Option(verificationData?.kyb?.companyPreviousNames)
          .filter((x) => x.length > 0)
          .fold(
            () => null,
            (codes) => (
              <Grid item xs={12}>
                <StyledWidget title="Company Previous Names">
                  <Table
                    data={codes}
                    head={[
                      { name: "companyName", title: "Company Name" },
                      {
                        name: "startDate",
                        title: "Start Date",
                        hiddenMobile: true,
                      },
                      { name: "endDate", title: "End Date" },
                    ]}
                    renderFields={{
                      startDate: ({ startDate }) =>
                        startDate ? (
                          <>
                            {moment.utc(startDate).local().format("DD-MM-yyyy")}
                          </>
                        ) : null,
                      endDate: ({ endDate }) =>
                        endDate ? (
                          <>
                            {moment.utc(endDate).local().format("DD-MM-yyyy")}
                          </>
                        ) : null,
                    }}
                  ></Table>
                </StyledWidget>
              </Grid>
            )
          )}
        {Option(verificationData?.kyb?.companyCorporateGroupings)
          .filter((x) => x.length > 0)
          .fold(
            () => null,
            (codes) => (
              <Grid item xs={12}>
                <StyledWidget title="Company Corporate Groupings">
                  <Table
                    data={codes}
                    hide={["referenceUrl"]}
                    head={[
                      { name: "name", title: "Name" },
                      { name: "wikipediaId", title: "Wikipedia Id" },
                      {
                        name: "updatedAt",
                        title: "Updated At",
                        hiddenMobile: true,
                      },
                      { name: "referenceUrl", title: "Reference Url" },
                    ]}
                    renderFields={{
                      name: ({ name, referenceUrl }) =>
                        Option(referenceUrl).fold(
                          () => <>{name}</>,
                          (url) => (
                            <a target="_blank" rel="noreferrer" href={url}>
                              {name}
                            </a>
                          )
                        ),
                    }}
                  ></Table>
                </StyledWidget>
              </Grid>
            )
          )}
        {Option(verificationData?.kyb?.companyIndustryCodes)
          .filter((x) => x.length > 0)
          .fold(
            () => null,
            (codes) => (
              <Grid item xs={12}>
                <StyledWidget title="Industry Codes">
                  <Table
                    data={codes}
                    head={[
                      { name: "code", title: "Code" },
                      { name: "description", title: "Description" },
                      {
                        name: "codeSchemeId",
                        title: "Code Scheme Id",
                        hiddenMobile: true,
                      },
                      {
                        name: "codeSchemeName",
                        title: "Code Scheme Name",
                        hiddenMobile: true,
                      },
                    ]}
                  ></Table>
                </StyledWidget>
              </Grid>
            )
          )}
        {Option(verificationData?.kyb?.companyTrademarkRegistrations)
          .filter((x) => x.length > 0)
          .fold(
            () => null,
            (codes) => (
              <Grid item xs={12}>
                <StyledWidget title="Trademark Registrations">
                  <Table
                    data={codes}
                    head={[
                      { name: "registerName", title: "Register Name" },
                      {
                        name: "markDetails",
                        title: "Mark Details",
                        hiddenMobile: true,
                      },
                      {
                        name: "registrationDate",
                        title: "Registration Date",
                        hiddenMobile: true,
                      },
                      { name: "expiryDate", title: "Expiry Date" },
                    ]}
                    renderFields={{
                      registrationDate: ({ registrationDate }) =>
                        registrationDate ? (
                          <>
                            {moment
                              .utc(registrationDate)
                              .local()
                              .format("DD-MM-yyyy")}
                          </>
                        ) : null,
                      expiryDate: ({ expiryDate }) =>
                        expiryDate ? (
                          <>
                            {moment
                              .utc(expiryDate)
                              .local()
                              .format("DD-MM-yyyy")}
                          </>
                        ) : null,
                    }}
                  ></Table>
                </StyledWidget>
              </Grid>
            )
          )}
      </Grid>
    </>
  );
};
